// src/components/OtpAuth.js
//
// Shared email OTP entry — two-step (email, then 6-digit code) auth flow.
// Used in three places: inline inside ResultCTA.js's primary CTA, as the
// entire content of the /auth login page (AuthPage.js), and inside the
// shared SignInModal.js (calculator + triage "Returning? Sign in"). Same
// component, same verification logic everywhere — one auth mechanism,
// not several built separately.
//
// Deliberately does NOT navigate or decide what happens after success —
// the parent supplies onSuccess(session) and decides where to go. Keeps
// this a pure "get me a verified session" building block, reusable
// regardless of what the caller needs to do once someone's logged in.
//
// Optional beforeSend(email): if provided, runs before the code is sent
// and can throw to block (the thrown message is shown as the form error).
// This is how SignInModal enforces its "must already have an account"
// gate without duplicating the email-entry UI — one input, one place
// that owns send/verify, gate logic is just an injected check in front
// of it. AuthPage and ResultCTA don't pass this, so they're unaffected.

import React, { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import posthog from '../lib/posthog'
import { validateEmail } from '../lib/emailValidation'
export default function OtpAuth({ initialEmail = '', onSuccess, submitLabel = 'Continue', beforeSend }) {
  const { sendOtp, verifyOtp } = useAuth()
  const [step, setStep]       = useState('email') // 'email' | 'code'
  const [email, setEmail]     = useState(initialEmail)
  const [code, setCode]       = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)

  async function handleSendCode() {
    const checked = validateEmail(email)
    if (!checked.ok) {
      setError(checked.error)
      return
    }
    const cleanEmail = checked.email
    setEmail(cleanEmail)
    setLoading(true)
    setError(null)
    try {
      if (beforeSend) {
        await beforeSend(cleanEmail)
      }
      const { error: otpError } = await sendOtp(cleanEmail)
      if (otpError) throw otpError
      posthog.capture('otp_code_requested')
      setStep('code')
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerify() {
    if (!code || code.length < 6) {
      setError('Enter the 6-digit code from your email.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const { data, error: verifyError } = await verifyOtp(email, code)
      if (verifyError) throw verifyError
      posthog.capture('otp_verified')
      onSuccess(data.session)
    } catch (err) {
      setError(err.message || "That code didn't work — check it and try again.")
    } finally {
      setLoading(false)
    }
  }

  function handleKeyDown(e, submitFn) {
    if (e.key === 'Enter') submitFn()
  }

  if (step === 'code') {
    return (
      <div className="otp-step">
        <p className="u-subtext">
          We sent a code to <strong>{email}</strong>.
        </p>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          className="app-input otp-code-input"
          placeholder="123456"
          value={code}
          onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(null) }}
          onKeyDown={e => handleKeyDown(e, handleVerify)}
          disabled={loading}
          autoFocus
        />
        {error && <p className="otp-error">{error}</p>}
        <button className="btn-primary otp-btn-full" onClick={handleVerify} disabled={loading}>
          {loading ? 'Verifying…' : `${submitLabel} →`}
        </button>
        <button
          className="otp-secondary-link"
          onClick={() => { setStep('email'); setCode(''); setError(null) }}
          disabled={loading}
        >
          Use a different email
        </button>
      </div>
    )
  }

  return (
    <div className="otp-step">
      <input
        type="email"
        className="app-input otp-email-input"
        placeholder="your@email.com"
        value={email}
        onChange={e => { setEmail(e.target.value); setError(null) }}
        onKeyDown={e => handleKeyDown(e, handleSendCode)}
        disabled={loading}
        autoFocus
      />
      {error && <p className="otp-error">{error}</p>}
      <button className="btn-primary otp-btn-full" onClick={handleSendCode} disabled={loading}>
        {loading ? 'Sending…' : 'Send code →'}
      </button>
    </div>
  )
}